import JSONApiService from "@/services/JSONApiService";
import { disablePluralizeForCall } from "@/services/utils";
import {
  APPLICATION_MODEL,
  applicationActions
} from "@/models/application-model";
import { ASSESSMENT_MODEL } from "@/models/assessment-model";
import { BOOKING_MODEL } from "@/models/booking-model";
import {
  include,
  CREATED_BY,
  AUTHOR,
  OPTIONS,
  QUESTION,
  ANSWER
} from "@/models/relationships";
import { BOOKING_APPLICATION_QUESTION_MODEL } from "@/models/booking-application-question-model";
import { applicationIncludes } from "@/services/modules/bookings/bookings";
import { COMMENT_MODEL } from "@/models/comment-model";
import { BOOKING_APPLICATION_QUESTIONS_ANSWER_MODEL } from "@/models/application-answer-model";
import APIService from "@/services/APIService";
import pluralize from "pluralize";

const { APPROVE, REJECT, SHORTLIST, WITHDRAW, UNSORTED } = applicationActions;

const callApplicationAction = (commit, applicationId, action, payload = {}) => {
  const api = JSONApiService(commit);
  api.one(APPLICATION_MODEL, applicationId);
  disablePluralizeForCall(api, () => api.all(action));
  return api.post(payload);
};

const applicationApi = commit => ({
  addAssessment: async ({ applicationId, assessment }) =>
    JSONApiService(commit)
      .all(ASSESSMENT_MODEL)
      .post(
        { ...assessment, application: { id: applicationId } },
        include(CREATED_BY)
      ),
  withdrawCandidate: async applicationId =>
    callApplicationAction(commit, applicationId, WITHDRAW),
  updateApplication: async application =>
    JSONApiService(commit).update(
      APPLICATION_MODEL,
      application,
      applicationIncludes
    ),
  approveCandidate: async applicationId =>
    callApplicationAction(commit, applicationId, APPROVE),
  shortlistCandidate: async applicationId =>
    callApplicationAction(commit, applicationId, SHORTLIST),
  unsortApplication: async applicationId =>
    callApplicationAction(commit, applicationId, UNSORTED),
  rejectCandidate: async ({ applicationId, reason }) =>
    APIService.client().post(
      `${pluralize(APPLICATION_MODEL)}/${applicationId}/${REJECT}`,
      {
        data: { attributes: { reason } }
      }
    ),
  createApplication: async ({ user, booking, utm_source }) =>
    JSONApiService(commit)
      .all(APPLICATION_MODEL)
      .post({ user, booking, utm_source }),
  getAllApplications: async params =>
    JSONApiService(commit)
      .all(APPLICATION_MODEL)
      .get({ ...params, ...include(BOOKING_MODEL) }),
  getBookingAvailableCandidates: async bookingId =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, bookingId)
      .all(applicationActions.AVAILABLE_CANDIDATES)
      .get(),
  addApplicationComment: async (applicationId, comment) =>
    JSONApiService(commit)
      .all(COMMENT_MODEL)
      .post(comment, include(AUTHOR)),
  getApplicationComments: async applicationId =>
    JSONApiService(commit)
      .one(APPLICATION_MODEL, applicationId)
      .all(COMMENT_MODEL)
      .get(include(AUTHOR)),
  getCurrentApplication: async (
    applicationId,
    includes = include(BOOKING_MODEL)
  ) =>
    JSONApiService(commit)
      .one(APPLICATION_MODEL, applicationId)
      .get(includes),
  getBookingApplicationQuestions: async bookingId =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, bookingId)
      .all(QUESTION)
      .get(include(OPTIONS)),
  addBookingApplicationQuestion: async data =>
    JSONApiService(commit)
      .all(BOOKING_APPLICATION_QUESTION_MODEL)
      .post(data),
  removeBookingApplicationQuestion: async id =>
    JSONApiService(commit)
      .one(BOOKING_APPLICATION_QUESTION_MODEL, id)
      .destroy(),
  createApplicationAnswer: async data =>
    JSONApiService(commit)
      .all(BOOKING_APPLICATION_QUESTIONS_ANSWER_MODEL)
      .post(data),
  updateApplicationAnswer: async (data, answerId) =>
    JSONApiService(commit)
      .one(BOOKING_APPLICATION_QUESTIONS_ANSWER_MODEL, answerId)
      .patch(data),
  getCurrentApplicationAnswers: async id =>
    JSONApiService(commit)
      .one(APPLICATION_MODEL, id)
      .all(ANSWER)
      .get(include(QUESTION))
});

export default applicationApi;
